import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import BasicInfo from "./basic-info/basic-info";
import { UserDetailsStyled } from "./user-details.styles";
import { usersCollection } from "../../../../services/firebase";
import { formatDate } from "../../../../utils/utils";
import UserHeader from "./components/user-header";
import useUser from "../../../../hooks/useUser";
import { Typography } from "@mui/material";
import { yupResolver } from "@hookform/resolvers/yup";
import { CreateUserValidations } from "../../../../constants/validations";
import { DEFAULT_WORK_SCHEDULE } from "../../../../constants/constants";

const UserDetails = () => {
  const { id } = useParams(); // docId from URL
  const [activeTab, setActiveTab] = useState("basic-info");
  const [isSaving, setIsSaving] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  const form = useForm({
    defaultValues: {
      firstName: "",
      surname: "",
      email: "",
      role: "",
      fullName: "",
      serviceGroup: "",
      permissions: "",
      employmentStatus: "",
      rate: "",
      cost: "",
      title: "",
      startDate: "",
      endDate: "",
      workSchedule: DEFAULT_WORK_SCHEDULE,
      userNotes: "",
      timezone: "",
      weeklyHours: "",
    },
    resolver: yupResolver(CreateUserValidations()),
  });
  const { reset } = form;
  const { data: userData, isLoading, error } = useUser(id);

  // Reset form when userData is loaded
  useEffect(() => {
    if (userData) {
      reset({
        ...userData,
        workSchedule:
          userData.workSchedule?.map((entry, index) => ({
            day: entry.day || DEFAULT_WORK_SCHEDULE[index].day, // Assign missing day values
            hours: entry.hours !== undefined ? entry.hours : 0, // Ensure hours are valid
          })) || DEFAULT_WORK_SCHEDULE, // Default if missing
      });
    }
  }, [userData, reset]);

  const handleSave = async () => {
    try {
      setIsSaving(true);
      const formData = form.getValues();
      const fullName =
        `${formData.firstName.trim()} ${formData.surname.trim()}`.trim();
      const formattedStartDate = formData.startDate
        ? formatDate(new Date(formData.startDate), "M/d/yyyy")
        : "";
      const formattedEndDate = formData.endDate
        ? formatDate(new Date(formData.endDate), "M/d/yyyy")
        : "";

      const updatedUserData = {
        firstName: formData.firstName,
        surname: formData.surname,
        email: formData.email,
        fullName: fullName,
        role: formData.role,
        serviceGroup: formData.serviceGroup,
        startDate: formattedStartDate,
        endDate: formattedEndDate,
        permissions: formData.permissions,
        employmentStatus: formData.employmentStatus,
        rate: formData.rate,
        cost: formData.cost,
        title: formData.title,
        workSchedule: formData.workSchedule,
        userNotes: formData.userNotes,
        timezone: formData.timezone,
        weeklyHours: formData.weeklyHours,
      };

      await usersCollection.doc(id).update(updatedUserData);
    } catch (error) {
      console.error("Error updating user:", error);
    } finally {
      setIsSaving(false);
      setIsEditMode(false);
    }
  };

  if (!userData) {
    return <div>Loading user details...</div>; // Show a loading state
  }

  return (
    <UserDetailsStyled>
      <UserHeader
        userDetails={userData}
        activeTab={activeTab}
        isEditMode={isEditMode}
        setIsEditMode={setIsEditMode}
        isSaving={isSaving}
        handleSave={handleSave}
        isSaveEnabled={form?.formState?.isValid}
      />

      <div className="tab-content">
        {activeTab === "basic-info" && (
          <BasicInfo
            refetch={() => {}}
            userData={userData}
            isEditMode={isEditMode}
            form={form}
          />
        )}
      </div>
      <div className="footer">
        <hr></hr>
        <div className="space-between">
          <div className="created">
            <Typography variant="body3">
              Created By: {userData.createdBy}
            </Typography>
            <Typography variant="body3">
              Created Date: {userData.dateCreated}
            </Typography>
          </div>
        </div>
      </div>
    </UserDetailsStyled>
  );
};

export default UserDetails;
